import React, { createContext, useContext, useState, useEffect, ReactNode, SetStateAction, Dispatch } from 'react';
import { User } from '../types';
import { createOrUpdateTelegramUser } from '../services/api';
import { initializeTelegramApp } from '../services/telegram';

interface UserContextType {
  user: User | null;
  setUser: Dispatch<SetStateAction<User | null>>;
  telegramUser: any | null;
  isLoading: boolean;
  error: string | null;
  refreshUser: () => Promise<void>;
  lastFreeSpin: string | null;
  lastSpinResult: any | null;
  refreshLastFreeSpin: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [telegramUser, setTelegramUser] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFreeSpin, setLastFreeSpin] = useState<string | null>(null);
  const [lastSpinResult, setLastSpinResult] = useState<any | null>(null);

  const loadUserData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Initialize Telegram Web App and get user data
      const tgUser = await initializeTelegramApp();
      setTelegramUser(tgUser);
      
      if (tgUser) {
        // Create or update user in our system
        const userData = await createOrUpdateTelegramUser({
          telegramId: tgUser.id.toString(),
          telegramUsername: tgUser.username || undefined,
          firstName: tgUser.firstName || undefined,
        });
        
        setUser(userData);
      } else {
        setError('Telegram user data not available');
      }
    } catch (err) {
      console.error('Error loading user data:', err);
      setError('Failed to load user data');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUser = async () => {
    await loadUserData();
  };

  const refreshLastFreeSpin = async () => {
    if (telegramUser) {
      try {
        const { getUserLastFreeSpin } = await import('../services/api');
        const data = await getUserLastFreeSpin();
        setLastFreeSpin(data.lastFreeSpin);
        setLastSpinResult(data.lastSpinResult);
      } catch (err) {
        console.error('Error loading last free spin data:', err);
      }
    }
  };

  useEffect(() => {
    loadUserData();
  }, []);

  // Load last free spin data when telegramUser changes
  useEffect(() => {
    if (telegramUser) {
      refreshLastFreeSpin();
    }
  }, [telegramUser]);

  const value = {
    user,
    setUser,
    telegramUser,
    isLoading,
    error,
    refreshUser,
    lastFreeSpin,
    lastSpinResult,
    refreshLastFreeSpin
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
